import { api } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../users/userTypes';
import type {
  AccessLevel,
  AccessLevelRequest,
  LevelGatedModule,
  SetUserAccessLevelsRequest,
  UserAccessLevel,
  UserAccessLevelRow,
} from './accessLevelTypes';

function toQueryString(query: ListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);

  /*
    Sent whenever the key is present, empty string included — an empty value is the reader
    saying "none of these", which matches no row. A truthiness check would drop it and the
    server would read the axis as unmentioned.
  */
  if (query.status !== undefined) params.set('status', query.status);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

export const accessLevelsApi = {
  /** Soft-deletes every rung at once; resolves with how many. Classified records keep theirs. */
  clear: () => api<number>('/api/access-levels/clear', { method: 'POST' }),

  list: (query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<AccessLevel>>(`/api/access-levels${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) =>
    api<AccessLevel>(`/api/access-levels/${id}`, { signal }),

  /** The ACTIVE rungs, strongest first, for a picker. */
  options: (signal?: AbortSignal) =>
    api<AccessLevel[]>('/api/access-levels/options', { signal }),

  /**
   * The modules whose records carry a level — the library a level picker reads.
   *
   * Off the registry, so a module gaining or losing gating changes every form that draws a
   * picker with no second list to keep in step.
   */
  modules: (signal?: AbortSignal) =>
    api<LevelGatedModule[]>('/api/access-levels/modules', { signal }),

  /** Every account and its clearance on ONE module — what a gated module's roster lists. */
  roster: (moduleKey: string, query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<UserAccessLevelRow>>(
      `/api/access-levels/modules/${encodeURIComponent(moduleKey)}/roster${toQueryString(query)}`,
      { signal },
    ),

  create: (request: AccessLevelRequest) =>
    api<AccessLevel>('/api/access-levels', { method: 'POST', body: request }),

  update: (id: string, request: AccessLevelRequest) =>
    api<AccessLevel>(`/api/access-levels/${id}`, { method: 'PUT', body: request }),

  /** Soft delete: the rung leaves the pickers. It declassifies nothing. */
  deactivate: (id: string) => api<void>(`/api/access-levels/${id}`, { method: 'DELETE' }),

  activate: (id: string) => api<void>(`/api/access-levels/${id}/restore`, { method: 'POST' }),

  /** One account's clearance on every gated module, tagged or not. */
  userLevels: (userId: string, signal?: AbortSignal) =>
    api<UserAccessLevel[]>(`/api/users/${userId}/access-levels`, { signal }),

  /**
   * Tags an account's clearance. Only the modules named are touched.
   *
   * The roster page sends one module and knows nothing about the others; a request that
   * rebuilt the whole set would have that page quietly clear every tag it cannot see.
   */
  setUserLevels: (userId: string, request: SetUserAccessLevelsRequest) =>
    api<UserAccessLevel[]>(`/api/users/${userId}/access-levels`, { method: 'PUT', body: request }),
};
